import {
  formatMoney,
  INSURED_SUB_GL_PER_1000,
  insuranceOwedByMonth,
  liabilityYear,
  monthName,
  owedCrewName,
  owedJobLabel,
  owedRateLine,
  paceSentence,
  standingWords,
  todayCentral,
  yearProgress,
  type PaidWorkOrder,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import { fmtDateOnly } from "../../lib/format";
import { useAsync } from "../../lib/hooks";
import { fetchPaidWorkOrders } from "../../lib/queries";

/**
 * What the crews the company paid cost its two insurance policies, by month.
 *
 * Workers comp is paid to the fund monthly on what uninsured crews were paid;
 * liability is audited at the end of the policy year. Each month says how much
 * went to crews with their own cover and how much to crews without, and what
 * that costs. The same figures go out by email on the 1st (ADR 0275). All the
 * arithmetic is `insuranceOwed.ts`'s; this only lays it out.
 *
 * `reloadKey` loads the paid work orders again whenever it changes: the payout
 * board above bumps it when a payout is marked paid or not paid, so a month's
 * figures follow the press instead of waiting for the screen to be reopened.
 */
export function InsuranceOwed({ reloadKey = 0 }: { reloadKey?: number } = {}) {
  const query = useAsync(fetchPaidWorkOrders, [reloadKey]);
  const [open, setOpen] = useState<string | null>(null);

  // Only the first load says it is working: a reload keeps the figures on
  // screen, and the month that was open stays open, until the new ones land.
  if (query.loading && query.data == null)
    return <div className="stub">{t("Working out insurance…")}</div>;

  // Lysa pays comp from these figures, so a failed load shows no figures at
  // all rather than zeros she could take for real.
  if (query.error) {
    return (
      <div className="block" style={{ marginBottom: 16 }}>
        <h2>{t("Insurance owed")}</h2>
        <ErrorNote error={String(query.error)} />
      </div>
    );
  }

  const today = todayCentral();
  const orders = (query.data ?? []) as unknown as PaidWorkOrder[];
  const months = insuranceOwedByMonth(orders, today);
  const year = liabilityYear(orders, today);

  return (
    <div className="block" style={{ marginBottom: 16 }}>
      <h2>{t("Insurance owed")}</h2>
      <div className="na-hint" style={{ marginBottom: 10 }}>
        {t(
          "Workers comp and general liability on the crews paid each month, by the day each crew was paid. A crew with its own current certificate costs no comp and ${rate} per $1,000 of liability; everyone else is charged the full rate for the trade. Certificates are read as they stand today.",
          { rate: INSURED_SUB_GL_PER_1000.toFixed(2) },
        )}
      </div>

      {months.length === 0 ? (
        <div className="tnone">{t("No crew has been marked paid yet.")}</div>
      ) : (
        <div className="ewrap">
          <table className="etable">
            <thead>
              <tr>
                <th>{t("Month")}</th>
                <th className="num">{t("Paid to crews")}</th>
                <th className="num">{t("With own comp")}</th>
                <th className="num">{t("Without comp")}</th>
                <th className="num">{t("Comp owed")}</th>
                <th className="num">{t("With own liability")}</th>
                <th className="num">{t("Without liability")}</th>
                <th className="num">{t("Liability")}</th>
              </tr>
            </thead>
            <tbody>
              {months.flatMap((month) => {
                const expanded = open === month.month;
                const rows = [
                  <tr key={month.month}>
                    <td>
                      <span
                        className="clink"
                        role="button"
                        aria-expanded={expanded}
                        onClick={() => setOpen(expanded ? null : month.month)}
                      >
                        {monthName(month.month)}
                      </span>
                    </td>
                    <td className="num">{formatMoney(month.paid)}</td>
                    <td className="num">
                      {formatMoney(month.workersComp.ownCover)}
                    </td>
                    <td className="num">
                      {formatMoney(month.workersComp.ours)}
                    </td>
                    <td className="num">
                      <b>{formatMoney(month.workersComp.owed)}</b>
                    </td>
                    <td className="num">
                      {formatMoney(month.generalLiability.ownCover)}
                    </td>
                    <td className="num">
                      {formatMoney(month.generalLiability.ours)}
                    </td>
                    <td className="num">
                      <b>{formatMoney(month.generalLiability.owed)}</b>
                    </td>
                  </tr>,
                ];
                if (expanded) {
                  for (const crew of month.crews) {
                    rows.push(
                      <tr key={`${month.month}-${crew.key}`}>
                        <td colSpan={8}>
                          <div className="an">
                            {owedCrewName(crew.crew)} —{" "}
                            {t("paid {amount}", {
                              amount: formatMoney(crew.paid),
                            })}
                          </div>
                          <div className="na-hint">
                            {t("comp {amount}", {
                              amount: formatMoney(crew.workersComp),
                            })}{" "}
                            ({t(standingWords(crew.wcStanding, "comp"))}) ·{" "}
                            {t("liability {amount}", {
                              amount: formatMoney(crew.generalLiability),
                            })}{" "}
                            ({t(standingWords(crew.glStanding, "liability"))})
                          </div>
                          {crew.orders.map((row) => (
                            <div
                              className="na-hint"
                              key={row.order.id}
                              style={{ marginTop: 4 }}
                            >
                              <div>
                                WO-{row.order.order_number ?? "?"} ·{" "}
                                {owedJobLabel(row.order)} ·{" "}
                                {t("paid {date}", {
                                  date: fmtDateOnly(row.paidOn),
                                })}{" "}
                                · {formatMoney(row.paid)}
                              </div>
                              {/* Each trade has its own rate, so each payout
                                  says which trade it was and what it cost. */}
                              <div style={{ paddingLeft: 14 }}>
                                {owedRateLine(row)}
                              </div>
                            </div>
                          ))}
                        </td>
                      </tr>,
                    );
                  }
                }
                return rows;
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Under the months, as ADR 0275 says. */}
      <div className="na-hint" style={{ marginTop: 12 }}>
        <b>{t("Liability policy year")}</b> ({fmtDateOnly(year.from)} {t("to")}{" "}
        {fmtDateOnly(year.to)}, {yearProgress(year)}):{" "}
        {t(
          "paid to crews {paid} ({own} with their own liability), liability {owed}",
          {
            paid: formatMoney(year.paid),
            own: formatMoney(year.paidOwnCover),
            owed: formatMoney(year.owed),
          },
        )}
        {year.owedOnPace != null &&
          t(", on pace for {amount}", { amount: formatMoney(year.owedOnPace) })}
        . {paceSentence(year)}
      </div>
    </div>
  );
}
